import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PlaceIcon from '@mui/icons-material/Place';
import BadgeIcon from '@mui/icons-material/Badge';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import DrawIcon from '@mui/icons-material/Draw';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PhoneIcon from '@mui/icons-material/Phone';
import ComboboxDatosMaestros from './ComboboxDatosMaestros';
import DatosMaestrosService from '../services/datosMaestrosService';
import {
    obtenerTurnoAnsesPorTramite,
    cerrarVistaTurnoAnses,
    listarHistorialPorTramite,
    crearTurnoAnses,
    editarTurnoAnses,
    cancelarTurnoAnses,
    registrarCitacionTurnoAnses,
    registrarResultadoPresentacion
} from '../services/turnoAnsesService';
import { subirArchivo, descargarArchivo } from '../services/archivoService';
import { usuarioService } from '../services/usuarioService';
import { esEstadoFinal, tipoEstado } from '../utils/flujoEstados';
import { TIPOS_TURNO_ANSES, esSinPresentacion, metaTipoTurnoAnses } from '../utils/tiposTurnoAnses';

const hoyISO = () => {
    const ahora = new Date();
    return new Date(ahora.getTime() - ahora.getTimezoneOffset() * 60000).toISOString().split('T')[0];
};

const formularioVacio = () => ({
    fecha: hoyISO(),
    hora: '',
    udaiLugar: '',
    idLugar: null,
    tipo: 'PRESENTACION',
    tieneTurnoOficial: true,
    idArchivoComprobante: null,
    nombreArchivoComprobante: '',
    offsetMinutos: '',
    requiereFirma: false,
    documentacionAFirmar: '',
    idUsuarioApoderado: '',
    apoderadoNombre: '',
    apoderadoDni: '',
    apoderadoRelacion: '',
    observaciones: '',
    horaEscribania: '',
    escribaniaObservacion: ''
});

const formatearFecha = (fechaISO) => {
    if (!fechaISO) return '';
    const partes = String(fechaISO).split('T')[0].split('-');
    if (partes.length !== 3) return fechaISO;
    return `${partes[2]}/${partes[1]}/${partes[0]}`;
};

const formatearFechaHora = (valor) => {
    if (!valor) return '';
    const fecha = new Date(valor);
    if (Number.isNaN(fecha.getTime())) return '';
    return fecha.toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const RESULTADO_META = {
    INGRESADO: { label: 'Ingresado', badge: 'bg-success' },
    RECHAZADO: { label: 'Rechazado', badge: 'bg-danger' },
    CONDICIONAL: { label: 'Condicional', badge: 'bg-warning text-dark' },
    NO_PRESENTADO: { label: 'No presentado', badge: 'bg-secondary' },
    PENDIENTE: { label: 'Pendiente', badge: 'bg-info' },
    // Resoluciones de ANSES: llegan después del ingreso y cierran el trámite.
    FAVORABLE: { label: 'Favorable', badge: 'bg-success' },
    DESFAVORABLE: { label: 'Desfavorable', badge: 'bg-danger' }
};

const metaResultado = (resultado) => RESULTADO_META[resultado] || RESULTADO_META.PENDIENTE;

const ESTADO_IDA = { PROGRAMADO: 'Programada', REALIZADO: 'Realizada', CANCELADO: 'Cancelada' };
const metaEstadoIda = (estado) => ESTADO_IDA[estado] || estado || '—';

/**
 * Tarjeta de la ida a ANSES dentro de la vista detallada de un trámite.
 *
 * La ida no es un trámite: es un hito propio que, al cargarse, genera además el evento de agenda
 * y la citación al estudio en el turnero. Una vez registrado el resultado, la tarjeta deja de
 * mostrar el detalle del turno y pasa a mostrar cómo salió.
 */
function TarjetaTurnoAnses({
    idTramite,
    nombreArea = 'este trámite',
    onCambio,
    nombreEstadoActual,
    onEstadoCambiado,
    // En reajuste la denegatoria de ANSES no termina nada: es el desenlace esperado del reclamo
    // administrativo y es lo que habilita ir a juicio. Lo decide el módulo, no la tarjeta.
    denegatoriaContinuaEnJuicio = false,
    // Si el trámite se cierra o no a partir de cómo salió la presentación. En RTI no: después
    // del ingreso viene la junta médica, y encima RTI no tiene "Cerrado favorable" sino
    // "Completo" y "Rechazado". Ofrecer ahí la resolución es ofrecer algo que no corresponde.
    ofreceResolucion = true,
    // Después de ANSES el cliente pasa por la escribanía a firmar la documental. Eso solo
    // existe en reajuste, así que el campo lo habilita el módulo: en los demás no hay tal paso
    // y el backend descarta el dato aunque llegue.
    pideEscribania = false,
    // Avisa al módulo si la presentación de este trámite ya tiene resultado (true/false), una vez
    // cargado el turno y cada vez que cambia. Lo usa Plan de pago para ordenar sus paneles.
    onResultadoRegistrado
}) {
    const [turno, setTurno] = useState(null);
    const [cargando, setCargando] = useState(true);
    const [guardando, setGuardando] = useState(false);
    const [modo, setModo] = useState(null); // 'crear' | 'editar' | 'reprogramar' | null
    const [datos, setDatos] = useState(formularioVacio());
    const [subiendoComprobante, setSubiendoComprobante] = useState(false);
    const [lugares, setLugares] = useState([]);
    const [usuarios, setUsuarios] = useState([]);
    const [mostrarDetalle, setMostrarDetalle] = useState(false);
    const [historial, setHistorial] = useState([]);
    const [verHistorial, setVerHistorial] = useState(false);

    // Cierre de una vista: lo único que queda de ella es qué se vio.
    const [observacionVista, setObservacionVista] = useState('');
    const [cerrandoVista, setCerrandoVista] = useState(false);
    // Reprogramar una vista necesita saber cuál: las vistas no son el turno vigente.
    const [vistaReprogramando, setVistaReprogramando] = useState(null);

    const [mostrarCancelacion, setMostrarCancelacion] = useState(false);
    const [motivoCancelacion, setMotivoCancelacion] = useState('');
    const [mostrarCitacion, setMostrarCitacion] = useState(false);
    const [observacionCitacion, setObservacionCitacion] = useState('');

    const [presentacionResultado, setPresentacionResultado] = useState(null);
    const [resultado, setResultado] = useState({ resultado: 'INGRESADO', numeroExpediente: '', motivoRechazo: '', observacion: '' });

    // Cierre del trámite desde acá: una vez que el expediente está en seguimiento, el desenlace
    // se conoce en esta pantalla (es donde se ve cómo salió la presentación), así que no tiene
    // sentido mandar al usuario al modal de historial para registrarlo.
    const [cierreElegido, setCierreElegido] = useState(null); // 'FAVORABLE' | 'DESFAVORABLE'
    const [comentarioCierre, setComentarioCierre] = useState('');
    const [cerrando, setCerrando] = useState(false);

    const cargarTurno = useCallback(async () => {
        setCargando(true);
        try {
            // Las dos cosas juntas: la ida vigente (lo que se opera) y el historial completo
            // (lo que pasó antes). El historial ya existía en el backend y no lo miraba nadie:
            // un turno cancelado quedaba guardado y sin ninguna pantalla donde verse.
            const [vigente, todas] = await Promise.all([
                obtenerTurnoAnsesPorTramite(idTramite),
                listarHistorialPorTramite(idTramite).catch(() => []),
            ]);
            setTurno(vigente);
            setHistorial(todas);
        } catch (error) {
            console.error('Error al cargar el turno ANSES:', error);
            toast.error('No se pudo cargar el turno ANSES del trámite.');
        } finally {
            setCargando(false);
        }
    }, [idTramite]);

    useEffect(() => {
        cargarTurno();
    }, [cargarTurno]);

    // Catálogos del formulario: las UDAI salen de datos maestros (igual que el turno ANSES de
    // RTI) y los usuarios sirven para marcar que va un abogado del estudio con poder.
    useEffect(() => {
        if (!modo) return;
        let cancelado = false;
        (async () => {
            try {
                const [respuestaLugares, respuestaUsuarios] = await Promise.all([
                    DatosMaestrosService.getLugares(),
                    usuarioService.getParaSeleccion()
                ]);
                if (cancelado) return;
                setLugares(Array.isArray(respuestaLugares.data) ? respuestaLugares.data : []);
                setUsuarios(Array.isArray(respuestaUsuarios.data) ? respuestaUsuarios.data : []);
            } catch (error) {
                console.error('Error al cargar los catálogos del turno:', error);
            }
        })();
        return () => { cancelado = true; };
    }, [modo]);

    // El cierre se registra como resultado de la presentación, no como un cambio de estado
    // suelto: así el badge del turno, su historial y el estado del trámite se mueven juntos.
    // El backend (TurnoAnsesService.registrarResultado) es el que mueve el trámite al cierre.
    const handleCerrarTramite = async () => {
        if (!cierreElegido || !presentacionPropia) return;
        setCerrando(true);
        try {
            const actualizado = await registrarResultadoPresentacion(
                turno.idTurnoAnses,
                presentacionPropia.id,
                { resultado: cierreElegido, observacion: comentarioCierre.trim() || null }
            );
            setTurno(actualizado);
            setCierreElegido(null);
            setComentarioCierre('');
            toast.success(cierreElegido === 'DESFAVORABLE' && denegatoriaContinuaEnJuicio
                ? 'Denegatoria registrada. El trámite pasa a instancia judicial.'
                : 'Resolución registrada. El trámite quedó cerrado.');
            // El padre relee la carpeta: el badge de estado y el banner de cierre viven afuera.
            if (onEstadoCambiado) onEstadoCambiado();
        } catch (error) {
            toast.error(error?.message || 'No se pudo registrar la resolución.');
        } finally {
            setCerrando(false);
        }
    };

    // La presentación de ESTE trámite dentro de la ida (la ida puede llevar varias).
    const presentacionPropia = useMemo(
        () => (turno?.presentaciones || []).find(p => p.idTramite === Number(idTramite)) || null,
        [turno, idTramite]
    );

    const resultadoRegistrado = presentacionPropia && presentacionPropia.resultado !== 'PENDIENTE';

    useEffect(() => {
        if (!cargando && onResultadoRegistrado) onResultadoRegistrado(Boolean(resultadoRegistrado));
    }, [cargando, resultadoRegistrado, onResultadoRegistrado]);

    // Vistas y citaciones de ANSES no llegan como "el turno del trámite" (el backend las excluye):
    // viven en su propia tarjeta, para que no tapen ni bloqueen el turno de presentación.
    const vistas = useMemo(
        () => (historial || []).filter(h => esSinPresentacion(h.tipo)),
        [historial]);
    const vistaAbierta = vistas.find(v => v.estado === 'PROGRAMADO') || null;

    // Los intentos anteriores: todo lo que trae el historial menos la ida que ya se muestra
    // arriba. Son las canceladas y las de intentos previos, que hasta ahora no se veían.
    const intentosPrevios = useMemo(
        () => (historial || []).filter((h) => !esSinPresentacion(h.tipo) && h.idTurnoAnses !== turno?.idTurnoAnses),
        [historial, turno]);

    // La resolución final solo tiene sentido sobre algo que ya entró en ANSES (mismo criterio
    // que valida el backend). Se mira la presentación y no solo el estado del trámite, porque
    // es la presentación la que se va a actualizar.
    // Cerrado puede venir por dos lados: por el resultado de la presentación (lo normal) o por un
    // cambio de estado hecho a mano desde el modal de historial.
    const cierreDePresentacion = presentacionPropia
        && (presentacionPropia.resultado === 'FAVORABLE'
            || (presentacionPropia.resultado === 'DESFAVORABLE' && !denegatoriaContinuaEnJuicio));
    const tramiteCerrado = cierreDePresentacion || esEstadoFinal(nombreEstadoActual);
    const cerradoFavorable = cierreDePresentacion
        ? presentacionPropia.resultado === 'FAVORABLE'
        : tipoEstado(nombreEstadoActual) === 'favorable';
    // Con el trámite ya en un estado final no queda nada que resolver (el plan de pago, por ejemplo,
    // pasa a Cerrado favorable al ingresar).
    const habilitaCierre = ofreceResolucion && !tramiteCerrado && presentacionPropia
        && ['INGRESADO', 'CONDICIONAL'].includes(presentacionPropia.resultado);

    const handleCerrarVista = async (vista) => {
        const texto = observacionVista.trim();
        if (!texto || cerrandoVista) return;
        setCerrandoVista(true);
        try {
            await cerrarVistaTurnoAnses(vista.idTurnoAnses, texto);
            setObservacionVista('');
            toast.success(`${metaTipoTurnoAnses(vista.tipo).etiquetaLarga} cerrada.`);
            await cargarTurno();   // relee turno + historial: la vista pasa a REALIZADA
            if (onCambio) onCambio();
        } catch (error) {
            toast.error(error?.response?.data?.mensaje || 'No se pudo cerrar la vista.');
        } finally {
            setCerrandoVista(false);
        }
    };

    // Cargar una vista o una citación aunque el trámite ya tenga turno de presentación: son cosas
    // distintas y no se excluyen. Se puede ir a mirar un expediente o acompañar una citación de
    // ANSES teniendo un turno para presentar.
    const abrirCreacionVista = (tipo = 'VISTA') => {
        setDatos({ ...formularioVacio(), tipo, tieneTurnoOficial: false });
        setModo('crear');
    };

    const abrirCreacion = () => {
        setDatos(formularioVacio());
        setModo('crear');
    };

    const abrirEdicion = () => {
        setDatos({
            fecha: turno.fecha ? String(turno.fecha).split('T')[0] : hoyISO(),
            // Con turno oficial se edita la hora del organismo; sin él, la de la citación.
            hora: (turno.tieneTurnoOficial ? turno.horaAnses : turno.horaCitacionEstudio) || '',
            udaiLugar: turno.udaiLugar || '',
            idLugar: turno.idLugar ?? null,
            tieneTurnoOficial: turno.tieneTurnoOficial,
            idArchivoComprobante: turno.idArchivoComprobante ?? null,
            nombreArchivoComprobante: turno.nombreArchivoComprobante || '',
            offsetMinutos: turno.offsetMinutos ?? '',
            requiereFirma: turno.requiereFirma,
            documentacionAFirmar: turno.documentacionAFirmar || '',
            idUsuarioApoderado: turno.idUsuarioApoderado ?? '',
            apoderadoNombre: turno.apoderadoNombre || '',
            apoderadoDni: turno.apoderadoDni || '',
            apoderadoRelacion: turno.apoderadoRelacion || '',
            observaciones: turno.observaciones || '',
            horaEscribania: turno.horaEscribania || '',
            escribaniaObservacion: turno.escribaniaObservacion || ''
        });
        setModo('editar');
    };

    const abrirReprogramacionVista = (v) => {
        setVistaReprogramando(v);
        setDatos({
            ...formularioVacio(),
            tipo: v.tipo || 'VISTA',
            tieneTurnoOficial: false,
            udaiLugar: v.udaiLugar || '',
            idLugar: v.idLugar ?? null,
        });
        setModo('reprogramar');
    };

    const abrirReprogramacion = () => {
        setVistaReprogramando(null);
        setDatos({
            ...formularioVacio(),
            udaiLugar: turno.udaiLugar || '',
            idLugar: turno.idLugar ?? null,
            tieneTurnoOficial: turno.tieneTurnoOficial,
            requiereFirma: turno.requiereFirma,
            documentacionAFirmar: turno.documentacionAFirmar || '',
            idUsuarioApoderado: turno.idUsuarioApoderado ?? '',
            apoderadoNombre: turno.apoderadoNombre || '',
            apoderadoDni: turno.apoderadoDni || '',
            apoderadoRelacion: turno.apoderadoRelacion || '',
            // La hora de escribanía queda atada al día que se está dejando atrás, así que se
            // vuelve a pedir. El detalle de qué firma y dónde sigue siendo el mismo.
            escribaniaObservacion: turno.escribaniaObservacion || ''
        });
        setModo('reprogramar');
    };

    const construirPayload = () => ({
        fecha: datos.fecha,
        horaAnses: datos.hora,
        idLugar: datos.idLugar,
        udaiLugar: datos.udaiLugar || null,
        tipo: datos.tipo,
        tieneTurnoOficial: datos.tieneTurnoOficial,
        idArchivoComprobante: datos.idArchivoComprobante,
        offsetMinutos: datos.offsetMinutos === '' ? null : Number(datos.offsetMinutos),
        requiereFirma: datos.requiereFirma,
        documentacionAFirmar: datos.documentacionAFirmar || null,
        idUsuarioApoderado: datos.idUsuarioApoderado === '' ? null : Number(datos.idUsuarioApoderado),
        apoderadoNombre: datos.apoderadoNombre || null,
        apoderadoDni: datos.apoderadoDni || null,
        apoderadoRelacion: datos.apoderadoRelacion || null,
        observaciones: datos.observaciones || null,
        horaEscribania: pideEscribania ? (datos.horaEscribania || null) : null,
        escribaniaObservacion: pideEscribania ? (datos.escribaniaObservacion || null) : null
    });

    const handleSubirComprobante = async (evento) => {
        const archivo = evento.target.files?.[0];
        if (!archivo) return;
        setSubiendoComprobante(true);
        try {
            const subido = await subirArchivo(idTramite, archivo);
            setDatos(actuales => ({
                ...actuales,
                idArchivoComprobante: subido?.idArchivo ?? null,
                nombreArchivoComprobante: subido?.nombre || archivo.name
            }));
            toast.success('Comprobante adjuntado.');
        } catch (error) {
            toast.error(error?.message || 'No se pudo subir el comprobante.');
        } finally {
            setSubiendoComprobante(false);
        }
    };

    const handleDescargarComprobante = async (idArchivo, nombre) => {
        try {
            const respuesta = await descargarArchivo(idArchivo);
            const url = window.URL.createObjectURL(new Blob([respuesta.data]));
            const enlace = document.createElement('a');
            enlace.href = url;
            enlace.download = nombre || 'comprobante';
            document.body.appendChild(enlace);
            enlace.click();
            enlace.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            toast.error('No se pudo descargar el comprobante.');
        }
    };

    const handleGuardar = async () => {
        if (!datos.fecha || !datos.hora) {
            toast.error(datos.tieneTurnoOficial
                ? 'La fecha y la hora del turno en ANSES son obligatorias.'
                : 'La fecha y la hora de la citación al estudio son obligatorias.');
            return;
        }
        setGuardando(true);
        try {
            const presentaciones = [{ idTramite: Number(idTramite) }];
            let guardado;
            if (modo === 'editar') {
                guardado = await editarTurnoAnses(turno.idTurnoAnses, construirPayload());
            } else if (modo === 'reprogramar') {
                guardado = await crearTurnoAnses({
                    ...construirPayload(), presentaciones,
                    idTurnoAnsesPrevio: (vistaReprogramando || turno).idTurnoAnses
                });
            } else {
                guardado = await crearTurnoAnses({ ...construirPayload(), presentaciones });
            }

            setVistaReprogramando(null);
            setTurno(guardado);
            if (guardado?.avisoCitacion) toast.info(guardado.avisoCitacion, { autoClose: 8000 });
            toast.success(modo === 'editar' ? 'Turno ANSES actualizado.' : 'Turno ANSES cargado.');
            setModo(null);
            if (onCambio) onCambio();
        } catch (error) {
            toast.error(error?.response?.data?.mensaje || 'No se pudo guardar el turno ANSES.');
        } finally {
            setGuardando(false);
        }
    };

    const abrirResultado = (presentacion) => {
        setPresentacionResultado(presentacion);
        setResultado({
            resultado: presentacion.resultado === 'PENDIENTE' ? 'INGRESADO' : presentacion.resultado,
            numeroExpediente: presentacion.numeroExpediente || '',
            motivoRechazo: presentacion.motivoRechazo || '',
            observacion: ''
        });
    };

    const handleGuardarResultado = async () => {
        if (resultado.resultado === 'INGRESADO' && !resultado.numeroExpediente.trim()) {
            toast.error('Para registrar un ingreso hace falta el número de expediente.');
            return;
        }
        if (resultado.resultado === 'RECHAZADO' && !resultado.motivoRechazo.trim()) {
            toast.error('Para registrar un rechazo hace falta el motivo.');
            return;
        }
        setGuardando(true);
        try {
            const actualizado = await registrarResultadoPresentacion(
                turno.idTurnoAnses, presentacionResultado.id, {
                    resultado: resultado.resultado,
                    numeroExpediente: resultado.numeroExpediente.trim() || null,
                    motivoRechazo: resultado.motivoRechazo.trim() || null,
                    observacion: resultado.observacion.trim() || null
                });
            setTurno(actualizado);
            setPresentacionResultado(null);
            toast.success('Resultado registrado.');
            if (onCambio) onCambio();
        } catch (error) {
            toast.error(error?.response?.data?.mensaje || 'No se pudo registrar el resultado.');
        } finally {
            setGuardando(false);
        }
    };

    const handleCancelarVista = async (vista) => {
        const meta = metaTipoTurnoAnses(vista.tipo);
        if (!window.confirm(`¿Cancelar ${meta.cierre}?`)) return;
        setGuardando(true);
        try {
            await cancelarTurnoAnses(vista.idTurnoAnses, null);
            toast.success(`${meta.etiquetaLarga} cancelada.`);
            await cargarTurno();
            if (onCambio) onCambio();
        } catch (error) {
            toast.error(error?.response?.data?.mensaje || 'No se pudo cancelar la vista.');
        } finally {
            setGuardando(false);
        }
    };

    const abrirCitacion = () => {
        setObservacionCitacion(turno.citacionObservacion || '');
        setMostrarCitacion(true);
    };

    // citado=false con nota es el intento que no llegó a la persona; sin nota y sin citar, el
    // turno vuelve a no decir nada, que es como nace.
    const guardarCitacion = async (citado) => {
        setGuardando(true);
        try {
            await registrarCitacionTurnoAnses(turno.idTurnoAnses, citado,
                observacionCitacion.trim() || null);
            setMostrarCitacion(false);
            toast.success(citado ? 'Se registró la citación.' : 'Se guardó la nota del contacto.');
            await cargarTurno();
            if (onCambio) onCambio();
        } catch (error) {
            toast.error(error?.response?.data?.mensaje || 'No se pudo registrar la citación.');
        } finally {
            setGuardando(false);
        }
    };

    const handleCancelar = async () => {
        setGuardando(true);
        try {
            await cancelarTurnoAnses(turno.idTurnoAnses, motivoCancelacion.trim() || null);
            setTurno(null);
            setMostrarCancelacion(false);
            setMotivoCancelacion('');
            toast.success('Se canceló el turno ANSES, junto con su citación al estudio.');
            if (onCambio) onCambio();
        } catch (error) {
            toast.error(error?.response?.data?.mensaje || 'No se pudo cancelar el turno.');
        } finally {
            setGuardando(false);
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────

    const renderFormulario = () => (
        <div className="mt-3 p-3 border rounded-3 bg-light">
            <h6 className="fw-semibold text-primary mb-3">
                {modo === 'editar' ? 'Editar turno en ANSES'
                    : modo === 'reprogramar' ? 'Nuevo turno en ANSES (tras el rechazo)'
                    : 'Cargar turno en ANSES'}
            </h6>

            {/* A qué se va. Solo al crear: cambiar el tipo de un turno ya cargado tendría
                implicancias sobre el estado del trámite que no se resuelven acá. */}
            {modo === 'crear' && (
                <div className="mb-3">
                    <label className="form-label fw-semibold">¿A qué se va?</label>
                    <div className="d-flex flex-wrap gap-3">
                        {TIPOS_TURNO_ANSES.map((op) => (
                            <div className="form-check" key={op.valor}>
                                <input
                                    type="radio"
                                    className="form-check-input"
                                    name="tipoTurnoAnsesTarjeta"
                                    id={`tipo-tarjeta-${op.valor}`}
                                    checked={datos.tipo === op.valor}
                                    onChange={() => setDatos({
                                        ...datos,
                                        tipo: op.valor,
                                        // Solo la presentación tiene turno del organismo: vista y
                                        // citación se cargan con la hora del turno en el estudio.
                                        tieneTurnoOficial: op.valor === 'PRESENTACION',
                                    })}
                                />
                                <label className="form-check-label" htmlFor={`tipo-tarjeta-${op.valor}`}>
                                    <i className={`bi ${op.icono} me-1`}></i>{op.etiqueta}
                                </label>
                            </div>
                        ))}
                    </div>
                    <small className="text-muted">{metaTipoTurnoAnses(datos.tipo).ayuda}</small>
                </div>
            )}

            {datos.tipo === 'PRESENTACION' && (
                <div className="form-check mb-3">
                    <input
                        type="checkbox"
                        className="form-check-input"
                        id="tieneTurnoOficial"
                        checked={datos.tieneTurnoOficial}
                        onChange={(e) => setDatos({ ...datos, tieneTurnoOficial: e.target.checked })}
                    />
                    <label className="form-check-label fw-semibold" htmlFor="tieneTurnoOficial">
                        Tiene turno dado por ANSES
                    </label>
                    <div>
                        <small className="text-muted">
                            {datos.tieneTurnoOficial
                                ? 'Se cita al cliente al estudio con la anticipación configurada.'
                                : 'Sin turno del organismo: se cita al cliente al estudio y se va a ANSES cuando se puede.'}
                        </small>
                    </div>
                </div>
            )}

            <div className="row g-3">
                <div className="col-md-4">
                    <label className="form-label fw-semibold">Fecha <span className="text-danger">*</span></label>
                    <input
                        type="date"
                        className="form-control"
                        value={datos.fecha}
                        onChange={(e) => setDatos({ ...datos, fecha: e.target.value })}
                    />
                    {datos.fecha && datos.fecha < hoyISO() && (
                        <small className="text-warning-emphasis d-block mt-1">
                            <i className="bi bi-clock-history me-1" />
                            Turno ya ocurrido: queda como constancia, sin citación en el turnero ni evento de agenda.
                        </small>
                    )}
                </div>
                <div className="col-md-4">
                    <label className="form-label fw-semibold">
                        {datos.tieneTurnoOficial ? 'Hora en ANSES' : 'Hora de citación al estudio'}
                        <span className="text-danger"> *</span>
                    </label>
                    <input
                        type="time"
                        className="form-control"
                        value={datos.hora}
                        onChange={(e) => setDatos({ ...datos, hora: e.target.value })}
                    />
                </div>
                {datos.tieneTurnoOficial && (
                    <div className="col-md-4">
                        <label className="form-label fw-semibold">Anticipación (min)</label>
                        <input
                            type="number"
                            min="0"
                            max="480"
                            className="form-control"
                            placeholder="Por defecto de la config"
                            value={datos.offsetMinutos}
                            onChange={(e) => setDatos({ ...datos, offsetMinutos: e.target.value })}
                        />
                        <small className="text-muted">Vacío = el valor configurado en el turnero.</small>
                    </div>
                )}

                <div className="col-md-6">
                    <ComboboxDatosMaestros
                        opciones={lugares}
                        valor={datos.udaiLugar}
                        onChange={(valor) => setDatos({ ...datos, udaiLugar: valor, idLugar: null })}
                        tipo="lugar"
                        tipoLugar="Oficina"
                        placeholder="Seleccioná o escribí una UDAI..."
                        label="UDAI / Lugar"
                        icon={PlaceIcon}
                    />
                </div>

                <div className="col-md-6">
                    <label className="form-label fw-semibold">Comprobante del turno</label>
                    {datos.idArchivoComprobante ? (
                        <div className="d-flex align-items-center gap-2">
                            <AttachFileIcon fontSize="small" className="text-success" />
                            <span className="fw-semibold">{datos.nombreArchivoComprobante || 'Comprobante adjunto'}</span>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary rounded-pill"
                                onClick={() => setDatos({ ...datos, idArchivoComprobante: null, nombreArchivoComprobante: '' })}
                                disabled={subiendoComprobante}
                            >
                                Quitar
                            </button>
                        </div>
                    ) : (
                        <input type="file" className="form-control" disabled={subiendoComprobante} onChange={handleSubirComprobante} />
                    )}
                    {subiendoComprobante && <small className="text-muted">Subiendo comprobante...</small>}
                </div>

                {pideEscribania && (
                    <>
                        <div className="col-md-4">
                            <label className="form-label fw-semibold">Hora en la escribanía</label>
                            <input
                                type="time"
                                className="form-control"
                                value={datos.horaEscribania}
                                onChange={(e) => setDatos({ ...datos, horaEscribania: e.target.value })}
                            />
                            <small className="text-muted">Opcional: si después de ANSES pasa a firmar.</small>
                        </div>
                        <div className="col-md-8">
                            <label className="form-label fw-semibold">Detalle de la escribanía</label>
                            <input
                                type="text"
                                className="form-control"
                                maxLength={300}
                                placeholder="Escribanía, dirección, qué firma..."
                                value={datos.escribaniaObservacion}
                                onChange={(e) => setDatos({ ...datos, escribaniaObservacion: e.target.value })}
                            />
                        </div>
                    </>
                )}

                <div className="col-12">
                    <div className="form-check">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            id="requiereFirma"
                            checked={datos.requiereFirma}
                            onChange={(e) => setDatos({ ...datos, requiereFirma: e.target.checked })}
                        />
                        <label className="form-check-label fw-semibold" htmlFor="requiereFirma">
                            Tiene que firmar documentación antes de ir
                        </label>
                        <div><small className="text-muted">Recepción lo va a ver marcado el día del turno.</small></div>
                    </div>
                </div>

                {datos.requiereFirma && (
                    <div className="col-12">
                        <label className="form-label fw-semibold">¿Qué documentación falta firmar?</label>
                        <textarea
                            className="form-control"
                            rows="2"
                            value={datos.documentacionAFirmar}
                            onChange={(e) => setDatos({ ...datos, documentacionAFirmar: e.target.value })}
                            placeholder="Para que recepción sepa qué papel buscar el día del turno..."
                        />
                    </div>
                )}

                <div className="col-12"><hr className="my-1" /></div>
                <div className="col-12">
                    <small className="text-muted fw-semibold">
                        Quién se presenta — completar solo si no va el titular
                    </small>
                </div>

                {/* Con un abogado del estudio el cliente no viene: el turno no ocupa lugar en la cola. */}
                <div className="col-md-6">
                    <label className="form-label">Va un abogado del estudio con poder</label>
                    <select
                        className="form-select"
                        value={datos.idUsuarioApoderado}
                        onChange={(e) => setDatos({ ...datos, idUsuarioApoderado: e.target.value })}
                    >
                        <option value="">No — se presenta el cliente</option>
                        {usuarios.map(usuario => (
                            <option key={usuario.id} value={usuario.id}>{usuario.nombre}</option>
                        ))}
                    </select>
                    {datos.idUsuarioApoderado && (
                        <small className="text-info">
                            No se genera turno en el turnero: el cliente no viene al estudio.
                        </small>
                    )}
                </div>
                <div className="col-12"></div>
                <div className="col-md-5">
                    <label className="form-label">Nombre y apellido</label>
                    <input
                        type="text"
                        className="form-control"
                        value={datos.apoderadoNombre}
                        onChange={(e) => setDatos({ ...datos, apoderadoNombre: e.target.value })}
                    />
                </div>
                <div className="col-md-3">
                    <label className="form-label">DNI</label>
                    <input
                        type="text"
                        className="form-control"
                        value={datos.apoderadoDni}
                        onChange={(e) => setDatos({ ...datos, apoderadoDni: e.target.value })}
                    />
                </div>
                <div className="col-md-4">
                    <label className="form-label">Relación / vínculo</label>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Madre, hijo, apoderado..."
                        value={datos.apoderadoRelacion}
                        onChange={(e) => setDatos({ ...datos, apoderadoRelacion: e.target.value })}
                    />
                </div>

                <div className="col-12">
                    <label className="form-label fw-semibold">Observaciones</label>
                    <textarea
                        className="form-control"
                        rows="2"
                        placeholder="Por ejemplo, el turno posterior en la escribanía..."
                        value={datos.observaciones}
                        onChange={(e) => setDatos({ ...datos, observaciones: e.target.value })}
                    />
                </div>
            </div>

            <div className="d-flex justify-content-end gap-2 mt-3">
                <button className="btn btn-outline-secondary rounded-pill px-4" onClick={() => { setModo(null); setVistaReprogramando(null); }} disabled={guardando}>
                    Volver
                </button>
                <button className="btn btn-primary rounded-pill px-4" onClick={handleGuardar} disabled={guardando}>
                    {guardando ? 'Guardando...' : (modo === 'editar' ? 'Guardar cambios' : 'Cargar turno')}
                </button>
            </div>
        </div>
    );

    // Una vez registrado el resultado, lo que importa es cómo salió, no los datos del turno.
    const renderDesenlace = () => {
        const meta = metaResultado(presentacionPropia.resultado);
        return (
            <>
                <div className="d-flex align-items-center gap-3 mb-3">
                    <span className={`badge rounded-pill fs-6 px-3 py-2 ${meta.badge}`}>{meta.label}</span>
                    <span className="text-muted">
                        Presentado el {formatearFecha(turno.fecha)}
                        {turno.udaiLugar ? ` en ${turno.udaiLugar}` : ''}
                    </span>
                </div>

                {/* El expediente se conserva cuando ANSES resuelve, así que se sigue mostrando
                    en los cierres: es con lo que se identifica el trámite ante el organismo. */}
                {presentacionPropia.numeroExpediente && (
                    <div className="alert alert-success border-0 rounded-3">
                        <strong>N° de expediente:</strong> {presentacionPropia.numeroExpediente}
                    </div>
                )}
                {presentacionPropia.resultado === 'RECHAZADO' && (
                    <div className="alert alert-danger border-0 rounded-3">
                        <strong>Motivo del rechazo:</strong> {presentacionPropia.motivoRechazo}
                    </div>
                )}
                {presentacionPropia.resultado === 'CONDICIONAL' && (
                    <div className="alert alert-warning border-0 rounded-3">
                        Ingresó <strong>por insistencia</strong>: ANSES no dio carátula ni expediente y va a
                        avisar si lo toma. Queda en seguimiento hasta registrar el resultado definitivo.
                    </div>
                )}

                {presentacionPropia.historial?.length > 0 && (
                    <div className="mb-3">
                        <small className="text-muted fw-semibold d-block mb-1">Historial</small>
                        <ul className="list-group list-group-flush">
                            {presentacionPropia.historial.map(registro => (
                                <li key={registro.id} className="list-group-item px-0 py-1 border-0">
                                    <span className={`badge rounded-pill me-2 ${metaResultado(registro.resultado).badge}`}>
                                        {metaResultado(registro.resultado).label}
                                    </span>
                                    <small className="text-muted">
                                        {formatearFechaHora(registro.registradoEn)}
                                        {registro.usuario ? ` · ${registro.usuario}` : ''}
                                        {registro.observacion ? ` · ${registro.observacion}` : ''}
                                    </small>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <div className="d-flex flex-wrap gap-2">
                    <button className="btn btn-outline-primary rounded-pill px-4" onClick={() => abrirResultado(presentacionPropia)} disabled={guardando}>
                        {presentacionPropia.resultado === 'CONDICIONAL' ? 'Registrar resultado definitivo' : 'Corregir resultado'}
                    </button>
                    {/* Sacar otro turno se ofrece ante cualquier desenlace que no sea favorable,
                        no solo tras un rechazo. Un cierre desfavorable no termina la relación con
                        ANSES: se vuelve a ir, con otra documentación o por otra vía. Antes el
                        botón solo aparecía en RECHAZADO y el trámite quedaba sin salida. */}
                    {presentacionPropia.resultado !== 'FAVORABLE' && (
                        <button className="btn btn-warning rounded-pill px-4 d-flex align-items-center gap-2"
                                onClick={abrirReprogramacion} disabled={guardando}>
                            <EditCalendarIcon fontSize="small" /> Sacar turno nuevo
                        </button>
                    )}
                    <button className="btn btn-link" onClick={() => setMostrarDetalle(!mostrarDetalle)}>
                        {mostrarDetalle ? 'Ocultar datos del turno' : 'Ver datos del turno'}
                    </button>
                </div>

                {mostrarDetalle && <div className="mt-3">{renderDatosTurno()}</div>}
            </>
        );
    };

    const renderDatosTurno = () => (
        <div className="row g-3">
            <div className="col-md-3">
                <small className="text-muted d-block"><CalendarMonthIcon fontSize="small" className="me-1" />Fecha</small>
                <strong>{formatearFecha(turno.fecha)}</strong>
            </div>
            {turno.tieneTurnoOficial && (
                <div className="col-md-3">
                    <small className="text-muted d-block"><AccessTimeIcon fontSize="small" className="me-1" />Hora en ANSES</small>
                    <strong>{turno.horaAnses || '-'}</strong>
                </div>
            )}
            <div className="col-md-3">
                <small className="text-muted d-block"><AccessTimeIcon fontSize="small" className="me-1" />Turno en el estudio</small>
                <strong className="text-primary">{turno.horaCitacionEstudio || '-'}</strong>
                {turno.citacionAjustada && (
                    <div><small className="text-warning">Ajustada al horario del turnero</small></div>
                )}
            </div>
            <div className="col-md-3">
                <small className="text-muted d-block"><PlaceIcon fontSize="small" className="me-1" />UDAI / Lugar</small>
                <strong>{turno.udaiLugar || 'No especificada'}</strong>
            </div>

            {turno.horaEscribania && (
                <div className="col-md-4">
                    <small className="text-muted d-block"><AccessTimeIcon fontSize="small" className="me-1" />Escribanía</small>
                    <strong>{turno.horaEscribania}</strong>
                    {turno.escribaniaObservacion && (
                        <div><small className="text-muted">{turno.escribaniaObservacion}</small></div>
                    )}
                </div>
            )}

            {!turno.tieneTurnoOficial && (
                <div className="col-md-4">
                    <small className="text-muted d-block">Modalidad</small>
                    <strong>Sin turno dado por ANSES</strong>
                </div>
            )}
            {turno.idArchivoComprobante && (
                <div className="col-md-4">
                    <small className="text-muted d-block"><AttachFileIcon fontSize="small" className="me-1" />Comprobante</small>
                    <button
                        type="button"
                        className="btn btn-link p-0 fw-semibold text-start"
                        onClick={() => handleDescargarComprobante(turno.idArchivoComprobante, turno.nombreArchivoComprobante)}
                    >
                        {turno.nombreArchivoComprobante || 'Descargar'}
                    </button>
                </div>
            )}
            {turno.apoderadoNombre && (
                <div className="col-md-5">
                    <small className="text-muted d-block"><BadgeIcon fontSize="small" className="me-1" />Se presenta</small>
                    <strong>{turno.apoderadoNombre}</strong>
                    <span className="text-muted">
                        {turno.apoderadoDni ? ` · DNI ${turno.apoderadoDni}` : ''}
                        {turno.apoderadoRelacion ? ` · ${turno.apoderadoRelacion}` : ''}
                    </span>
                </div>
            )}
            {turno.usuarioApoderadoNombre && (
                <div className="col-md-5">
                    <small className="text-muted d-block">Va con poder del estudio</small>
                    <strong>{turno.usuarioApoderadoNombre}</strong>
                    <div><small className="text-muted">No ocupa lugar en el turnero.</small></div>
                </div>
            )}
            {turno.intento > 1 && (
                <div className="col-md-2">
                    <small className="text-muted d-block">Intento</small>
                    <strong>{turno.intento}°</strong>
                </div>
            )}
            {turno.observaciones && (
                <div className="col-12">
                    <div className="alert alert-light border rounded-3 mb-0">
                        <small className="text-muted d-block fw-semibold">Observaciones</small>
                        {turno.observaciones}
                    </div>
                </div>
            )}
        </div>
    );

    const renderTurnoPendiente = () => (
        <>
            {turno.requiereFirma && !turno.firmaResuelta && (
                <div className="alert alert-warning border-0 rounded-3 d-flex align-items-start gap-2 mb-3">
                    <DrawIcon fontSize="small" />
                    <div>
                        <span>El cliente tiene que <strong>firmar documentación</strong> antes de ir a ANSES.</span>
                        {turno.documentacionAFirmar && (
                            <div className="mt-1"><strong>Falta:</strong> {turno.documentacionAFirmar}</div>
                        )}
                    </div>
                </div>
            )}

            {/* Sin registro no se muestra ningún estado: el turno simplemente no dice si se
                lo citó. Afirmar "sin citar" sería falso para todos los turnos que se citaron
                por fuera del sistema, que son los que ya existen. */}
            {turno.citado ? (
                <div className="alert alert-success border-0 rounded-3 d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                    <div className="d-flex align-items-center gap-2">
                        <PhoneIcon fontSize="small" />
                        <div>
                            <span className="badge rounded-pill bg-success me-2">Citado</span>
                            <span className="text-muted small">
                                {turno.citadoEn ? formatearFechaHora(turno.citadoEn) : ''}
                                {turno.citacionObservacion ? ` · ${turno.citacionObservacion}` : ''}
                            </span>
                        </div>
                    </div>
                    <button className="btn btn-sm btn-outline-dark rounded-pill" onClick={abrirCitacion} disabled={guardando}>
                        Editar
                    </button>
                </div>
            ) : (
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                    {turno.citacionObservacion
                        ? <small className="text-muted">
                            <PhoneIcon fontSize="small" className="me-1" />
                            Se intentó avisarle: {turno.citacionObservacion}
                          </small>
                        : <span />}
                    <button className="btn btn-sm btn-outline-secondary rounded-pill d-flex align-items-center gap-2"
                            onClick={abrirCitacion} disabled={guardando}>
                        <PhoneIcon fontSize="small" /> Registrar citación
                    </button>
                </div>
            )}

            {renderDatosTurno()}

            <div className="mt-4">
                <h6 className="fw-semibold text-secondary">Qué se presenta</h6>
                <ul className="list-group mb-2">
                    {(turno.presentaciones || []).map(presentacion => (
                        <li key={presentacion.id} className="list-group-item d-flex justify-content-between align-items-center">
                            <span>
                                <strong>{presentacion.tipoTramite}</strong>
                                {presentacion.idTramite
                                    ? <span className="text-muted"> · trámite #{presentacion.idTramite}</span>
                                    : <span className="text-muted fst-italic"> · sin carpeta abierta</span>}
                                {presentacion.numeroExpediente && (
                                    <span className="text-muted"> · Exp. {presentacion.numeroExpediente}</span>
                                )}
                            </span>
                            <span className="d-flex align-items-center gap-2">
                                <span className={`badge rounded-pill ${metaResultado(presentacion.resultado).badge}`}>
                                    {metaResultado(presentacion.resultado).label}
                                </span>
                                <button
                                    className="btn btn-sm btn-outline-primary rounded-pill"
                                    onClick={() => abrirResultado(presentacion)}
                                    disabled={guardando}
                                >
                                    {presentacion.resultado === 'PENDIENTE' ? 'Registrar resultado' : 'Corregir'}
                                </button>
                            </span>
                        </li>
                    ))}
                </ul>
            </div>

            {/* Las tres acciones del turno, como en RTI. Editar y reprogramar NO son lo mismo:
                editar corrige datos que se cargaron mal y no deja rastro en el trámite;
                reprogramar es que el turno se movió de fecha, y eso sí queda en el historial. */}
            <div className="d-flex flex-wrap gap-2 mt-3">
                <button className="btn btn-outline-primary rounded-pill px-4 d-flex align-items-center gap-2"
                        onClick={abrirEdicion} disabled={guardando} title="Corregir datos mal cargados">
                    <EditCalendarIcon fontSize="small" /> Editar turno
                </button>
                <button className="btn btn-outline-warning rounded-pill px-4 d-flex align-items-center gap-2"
                        onClick={abrirReprogramacion} disabled={guardando}
                        title="Se movió la fecha: queda registrado en el historial del trámite">
                    <EditCalendarIcon fontSize="small" /> Reprogramar
                </button>
                <button className="btn btn-outline-danger rounded-pill px-4 d-flex align-items-center gap-2"
                        onClick={() => setMostrarCancelacion(true)} disabled={guardando}>
                    <EventBusyIcon fontSize="small" /> Cancelar
                </button>
            </div>
        </>
    );

    return (
        <div className="row mb-3">
            <Modal show={mostrarCitacion} onHide={() => setMostrarCitacion(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Citación del cliente</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted">
                        Dejá registrado que ya se le avisó que tiene turno. No cambia el turno ni
                        el estado del trámite.
                    </p>
                    <label className="form-label fw-semibold">Observación (opcional)</label>
                    <textarea
                        className="form-control"
                        rows={2}
                        maxLength={500}
                        placeholder="No contesta las llamadas, pidió que lo llamemos el lunes..."
                        value={observacionCitacion}
                        onChange={(e) => setObservacionCitacion(e.target.value)}
                    />
                    <small className="text-muted">
                        Si todavía no se lo pudo contactar, guardá solo la nota: queda el rastro del
                        intento sin darlo por citado.
                    </small>
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-secondary rounded-pill" onClick={() => setMostrarCitacion(false)} disabled={guardando}>
                        Cerrar
                    </button>
                    <button className="btn btn-outline-secondary rounded-pill" onClick={() => guardarCitacion(false)} disabled={guardando}>
                        {turno?.citado ? 'Quitar la citación' : 'Guardar solo la nota'}
                    </button>
                    <button className="btn btn-success rounded-pill" onClick={() => guardarCitacion(true)} disabled={guardando}>
                        Marcar como citado
                    </button>
                </Modal.Footer>
            </Modal>

            <Modal show={mostrarCancelacion} onHide={() => setMostrarCancelacion(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Cancelar el turno ANSES</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        Se cancela también la citación al estudio en el turnero y se borra el evento
                        de la agenda. Esta acción no se puede deshacer.
                    </p>
                    <label className="form-label fw-semibold">Motivo (opcional)</label>
                    <textarea
                        className="form-control"
                        rows="2"
                        value={motivoCancelacion}
                        onChange={(e) => setMotivoCancelacion(e.target.value)}
                        placeholder="Por ejemplo: ANSES reprogramó el turno..."
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setMostrarCancelacion(false)} disabled={guardando}>
                        Volver
                    </Button>
                    <Button variant="danger" onClick={handleCancelar} disabled={guardando}>
                        {guardando ? 'Cancelando...' : 'Cancelar el turno'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={Boolean(presentacionResultado)} onHide={() => setPresentacionResultado(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>¿Cómo salió la presentación?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {presentacionResultado && (
                        <p className="text-muted">
                            {presentacionResultado.tipoTramite}
                            {presentacionResultado.idTramite ? ` · trámite #${presentacionResultado.idTramite}` : ''}
                        </p>
                    )}

                    <label className="form-label fw-semibold">Resultado</label>
                    <select
                        className="form-select mb-3"
                        value={resultado.resultado}
                        onChange={(e) => setResultado({ ...resultado, resultado: e.target.value })}
                    >
                        <option value="INGRESADO">Ingresado — ANSES lo recibió</option>
                        <option value="CONDICIONAL">Condicional — ingreso por insistencia, avisan después</option>
                        <option value="RECHAZADO">Rechazado — hay que volver</option>
                        <option value="NO_PRESENTADO">No se presentó</option>
                        {/* Los cierres no se eligen desde acá (van por "Resolución de ANSES"),
                            pero se listan si la presentación ya está resuelta, para poder
                            corregir el valor sin que el select quede mostrando otra cosa. */}
                        {cierreDePresentacion && (
                            <>
                                <option value="FAVORABLE">Favorable — ANSES resolvió a favor</option>
                                <option value="DESFAVORABLE">Desfavorable — denegado o desistido</option>
                            </>
                        )}
                    </select>

                    {resultado.resultado === 'INGRESADO' && (
                        <>
                            <label className="form-label fw-semibold">N° de expediente</label>
                            <input
                                type="text"
                                className="form-control"
                                value={resultado.numeroExpediente}
                                onChange={(e) => setResultado({ ...resultado, numeroExpediente: e.target.value })}
                                placeholder="Expediente que asignó ANSES"
                            />
                        </>
                    )}

                    {resultado.resultado === 'CONDICIONAL' && (
                        <div className="alert alert-warning border-0 rounded-3 py-2">
                            En el ingreso por insistencia ANSES no da carátula ni expediente todavía. Queda
                            en seguimiento hasta que avisen.
                        </div>
                    )}

                    {resultado.resultado === 'RECHAZADO' && (
                        <>
                            <label className="form-label fw-semibold">Motivo del rechazo</label>
                            <textarea
                                className="form-control"
                                rows="3"
                                value={resultado.motivoRechazo}
                                onChange={(e) => setResultado({ ...resultado, motivoRechazo: e.target.value })}
                                placeholder="Qué faltó o qué hubo que corregir..."
                            />
                        </>
                    )}

                    <label className="form-label fw-semibold mt-3">Observación</label>
                    <textarea
                        className="form-control"
                        rows="2"
                        value={resultado.observacion}
                        onChange={(e) => setResultado({ ...resultado, observacion: e.target.value })}
                        placeholder="Queda registrada en el historial..."
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setPresentacionResultado(null)} disabled={guardando}>
                        Volver
                    </Button>
                    <Button variant="primary" onClick={handleGuardarResultado} disabled={guardando}>
                        {guardando ? 'Guardando...' : 'Registrar'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <div className="col-12">
                <div className="card shadow-lg border-0 rounded-4 p-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h4 className="fw-bold text-primary mb-0">
                            <i className="bi bi-building me-2"></i>Turno en ANSES
                        </h4>
                        {!cargando && modo !== 'crear' && (
                            <div className="d-flex flex-wrap gap-2">
                                {!turno && (
                                    <button className="btn btn-outline-primary rounded-pill fw-semibold" onClick={abrirCreacion}>
                                        + Sacar turno ANSES
                                    </button>
                                )}
                                {/* Vista y citación van aparte y siempre: tener un turno para
                                    presentar no impide ir a mirar un expediente ni acompañar una
                                    citación de ANSES. */}
                                <button className="btn btn-outline-secondary rounded-pill fw-semibold" onClick={() => abrirCreacionVista('VISTA')}>
                                    <i className="bi bi-eyeglasses me-1"></i>+ Cargar vista
                                </button>
                                <button className="btn btn-outline-secondary rounded-pill fw-semibold" onClick={() => abrirCreacionVista('CITACION')}>
                                    <i className="bi bi-envelope-paper me-1"></i>+ Cargar citación de ANSES
                                </button>
                            </div>
                        )}
                    </div>

                    {cargando ? (
                        <div className="text-center py-3">
                            <div className="spinner-border text-primary" role="status"></div>
                            <p className="mt-2 text-muted mb-0">Cargando turno ANSES...</p>
                        </div>
                    ) : (modo === 'crear' || vistaReprogramando) ? (
                        renderFormulario()
                    ) : turno ? (
                        (modo === 'editar' || modo === 'reprogramar') ? renderFormulario()
                            : resultadoRegistrado ? renderDesenlace()
                            : renderTurnoPendiente()
                    ) : (
                        <div className="alert alert-info border-0 rounded-3 mb-0">
                            Todavía no se cargó el turno en ANSES para {nombreArea}. Al cargarlo se genera
                            automáticamente la citación al estudio en el turnero y el evento en la agenda.
                        </div>
                    )}

                    {/* Ya cerrado: la tarjeta deja de ofrecer el cierre y pasa a mostrar cómo
                        terminó, para que el desenlace se vea también acá y no solo en la ficha. */}
                    {tramiteCerrado && (
                        <div className={`alert ${cerradoFavorable ? 'alert-success' : 'alert-danger'} border-0 rounded-3 d-flex align-items-center gap-3 mt-4 mb-0`}>
                            <i className={`bi ${cerradoFavorable ? 'bi-check-circle-fill' : 'bi-x-octagon-fill'} fs-4`}></i>
                            <div>
                                <strong>{nombreEstadoActual}</strong>
                                <div className="small">
                                    {cerradoFavorable
                                        ? 'ANSES resolvió a favor. El trámite quedó cerrado.'
                                        : 'El trámite se cerró sin resolución favorable.'}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Cierre del trámite: solo con el expediente en seguimiento, que es cuando
                        ANSES ya resolvió (o está por resolver) y se conoce el desenlace. */}
                    {habilitaCierre && (
                        <div className="border-top mt-4 pt-4">
                            <h6 className="fw-semibold text-secondary">Resolución de ANSES</h6>
                            <p className="text-muted small">
                                El expediente está en seguimiento. Cuando ANSES resuelva, registrá acá
                                cómo terminó: queda en el historial de la presentación
                                {denegatoriaContinuaEnJuicio
                                    ? ' y mueve el trámite. Si deniegan, pasa a instancia judicial: no se cierra.'
                                    : ' y cierra el trámite.'}
                            </p>

                            <div className="d-flex flex-wrap gap-2">
                                {['FAVORABLE', 'DESFAVORABLE'].map((opcion) => {
                                    const favorable = opcion === 'FAVORABLE';
                                    const activo = cierreElegido === opcion;
                                    return (
                                        <button
                                            key={opcion}
                                            className={`btn rounded-pill px-4 d-flex align-items-center gap-2 ${
                                                activo
                                                    ? (favorable ? 'btn-success' : 'btn-danger')
                                                    : (favorable ? 'btn-outline-success' : 'btn-outline-danger')
                                            }`}
                                            disabled={cerrando}
                                            onClick={() => {
                                                setCierreElegido(activo ? null : opcion);
                                                setComentarioCierre('');
                                            }}
                                        >
                                            <i className={`bi ${favorable ? 'bi-check-circle-fill' : 'bi-x-octagon-fill'}`}></i>
                                            {metaResultado(opcion).label}
                                        </button>
                                    );
                                })}
                            </div>

                            {cierreElegido && (
                                <div className="mt-3">
                                    <label className="form-label fw-semibold">
                                        Detalle de la resolución <span className="text-muted fw-normal">(opcional)</span>
                                    </label>
                                    <textarea
                                        className="form-control"
                                        rows="2"
                                        value={comentarioCierre}
                                        onChange={(e) => setComentarioCierre(e.target.value)}
                                        placeholder={`Qué resolvió ANSES (${metaResultado(cierreElegido).label.toLowerCase()})...`}
                                        disabled={cerrando}
                                        maxLength="1000"
                                    />
                                    <div className="d-flex justify-content-end gap-2 mt-2">
                                        <Button
                                            variant="outline-secondary"
                                            onClick={() => { setCierreElegido(null); setComentarioCierre(''); }}
                                            disabled={cerrando}
                                        >
                                            Cancelar
                                        </Button>
                                        <Button
                                            variant={cierreElegido === 'FAVORABLE' ? 'success' : 'danger'}
                                            onClick={handleCerrarTramite}
                                            disabled={cerrando}
                                        >
                                            {cerrando
                                                ? 'Registrando...'
                                                : (cierreElegido === 'DESFAVORABLE' && denegatoriaContinuaEnJuicio
                                                    ? 'Registrar y pasar a instancia judicial'
                                                    : 'Registrar resolución')}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Historial de idas: lo que se canceló o se reprogramó. Va plegado porque lo
                    habitual es mirar la ida vigente; se abre cuando alguien pregunta qué pasó
                    con el turno anterior. */}
                {intentosPrevios.length > 0 && (
                    <div className="border-top px-4 py-3">
                        <button
                            className="btn btn-link p-0 text-decoration-none fw-semibold"
                            onClick={() => setVerHistorial(!verHistorial)}
                        >
                            <i className={`bi bi-clock-history me-2`}></i>
                            {intentosPrevios.length === 1
                                ? '1 turno anterior'
                                : `${intentosPrevios.length} turnos anteriores`}
                            <i className={`bi bi-chevron-${verHistorial ? 'up' : 'down'} ms-2 small`}></i>
                        </button>

                        {verHistorial && (
                            <div className="mt-3 d-flex flex-column gap-2">
                                {intentosPrevios.map((h) => {
                                    const cancelada = h.estado === 'CANCELADO';
                                    const propia = (h.presentaciones || [])
                                        .find(p => p.idTramite === Number(idTramite));
                                    return (
                                        <div
                                            key={h.idTurnoAnses}
                                            className="border rounded-3 px-3 py-2 d-flex flex-wrap align-items-center gap-2"
                                            style={{ background: cancelada ? '#fdf6f5' : '#f8f9fa' }}
                                        >
                                            <span className={`badge ${cancelada ? 'bg-danger' : 'bg-secondary'}`}>
                                                {cancelada ? 'Cancelada' : metaEstadoIda(h.estado)}
                                            </span>
                                            <span className="fw-semibold">{formatearFecha(h.fecha)}</span>
                                            {h.horaAnses && <span className="text-muted">{h.horaAnses}</span>}
                                            {h.udaiLugar && <span className="text-muted small">· {h.udaiLugar}</span>}
                                            {h.intento > 1 && (
                                                <span className="text-muted small">· intento {h.intento}</span>
                                            )}
                                            {propia && propia.resultado !== 'PENDIENTE' && (
                                                <span className={`badge ${metaResultado(propia.resultado).badge}`}>
                                                    {metaResultado(propia.resultado).label}
                                                </span>
                                            )}
                                            {/* El motivo es lo que se pregunta al ver una cancelación. */}
                                            {cancelada && (
                                                <div className="w-100 small text-muted">
                                                    {h.motivoCancelacion
                                                        ? `Motivo: ${h.motivoCancelacion}`
                                                        : 'Sin motivo registrado.'}
                                                    {h.canceladoEn && ` · ${formatearFechaHora(h.canceladoEn)}`}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Vistas y citaciones de ANSES van en su propia tarjeta, no dentro de la del turno de
                presentación. No presentan nada ni mueven el trámite: si compartieran tarjeta,
                tapaban el turno y no dejaban sacar otro. Una vez cerradas se muestran en una línea
                con lo que pasó; solo la que sigue abierta pide la observación. */}
            {vistas.length > 0 && (
                <div className="card shadow-sm border-0 rounded-4 p-3 mt-3">
                    <h6 className="fw-semibold text-secondary mb-2">
                        <i className={`bi ${vistas.length === 1 ? metaTipoTurnoAnses(vistas[0].tipo).icono : 'bi-building'} me-2`}></i>
                        {vistas.length === 1
                            ? metaTipoTurnoAnses(vistas[0].tipo).etiquetaLarga
                            : `Vistas y citaciones de ANSES (${vistas.length})`}
                    </h6>

                    <div className="d-flex flex-column gap-2">
                        {vistas.map(v => (
                            <div key={v.idTurnoAnses}
                                 className="border rounded-3 px-3 py-2 d-flex flex-wrap align-items-center gap-2"
                                 style={{ background: v.estado === 'CANCELADO' ? '#fdf6f5' : '#f8f9fa' }}>
                                <span className={`badge ${v.estado === 'REALIZADO' ? 'bg-success'
                                    : v.estado === 'CANCELADO' ? 'bg-danger' : 'bg-primary'}`}>
                                    {v.estado === 'REALIZADO' ? 'Realizada'
                                        : v.estado === 'CANCELADO' ? 'Cancelada' : 'Programada'}
                                </span>
                                <span className="badge bg-light text-dark border">
                                    <i className={`bi ${metaTipoTurnoAnses(v.tipo).icono} me-1`}></i>
                                    {metaTipoTurnoAnses(v.tipo).badge}
                                </span>
                                <span className="fw-semibold">{formatearFecha(v.fecha)}</span>
                                {v.horaCitacionEstudio && <span className="text-muted">{v.horaCitacionEstudio}</span>}
                                {v.udaiLugar && <span className="text-muted small">· {v.udaiLugar}</span>}
                                {v.observaciones && (
                                    <div className="w-100 small text-muted">{v.observaciones}</div>
                                )}
                                {v.estado === 'PROGRAMADO' && (
                                    <span className="d-flex gap-2 ms-auto">
                                        <button
                                            className="btn btn-sm btn-outline-warning rounded-pill"
                                            onClick={() => abrirReprogramacionVista(v)}
                                            disabled={guardando}
                                            title="Se movió la fecha: queda registrado en el historial"
                                        >
                                            Reprogramar
                                        </button>
                                        <button
                                            className="btn btn-sm btn-outline-danger rounded-pill"
                                            onClick={() => handleCancelarVista(v)}
                                            disabled={guardando}
                                        >
                                            Cancelar
                                        </button>
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Solo la que sigue abierta pide el cierre. */}
                    {vistaAbierta && (
                        <div className="mt-3">
                            <label className="form-label fw-semibold">
                                Cerrar {metaTipoTurnoAnses(vistaAbierta.tipo).cierre} del {formatearFecha(vistaAbierta.fecha)}
                            </label>
                            <textarea
                                className="form-control"
                                rows="2"
                                maxLength="1000"
                                placeholder={metaTipoTurnoAnses(vistaAbierta.tipo).placeholderCierre}
                                value={observacionVista}
                                onChange={(e) => setObservacionVista(e.target.value)}
                                disabled={cerrandoVista}
                            />
                            <div className="d-flex justify-content-end mt-2">
                                <Button
                                    variant="primary"
                                    onClick={() => handleCerrarVista(vistaAbierta)}
                                    disabled={cerrandoVista || !observacionVista.trim()}
                                >
                                    {cerrandoVista ? 'Guardando...' : `Cerrar ${metaTipoTurnoAnses(vistaAbierta.tipo).cierre}`}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default TarjetaTurnoAnses;
